import React, { useEffect, useState, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import MilitaryTechRoundedIcon from "@mui/icons-material/MilitaryTechRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import API from "../../api/api";

function fmtINR(val) {
  const n = Number(val || 0);
  return Number.isFinite(n)
    ? `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "₹0.00";
}

export default function AdminAgentTrainingVouchers() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({ count: 0, results: [], total_value: 0 });
  const [q, setQ] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await API.get("/business/admin/spp/agent-training-vouchers/");
      setData(res?.data || { count: 0, results: [], total_value: 0 });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load activated agent training vouchers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCopy = (text, id) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const filteredRows = useMemo(() => {
    const list = data.results || [];
    if (!q.trim()) return list;
    const term = q.toLowerCase();
    return list.filter(
      (r) =>
        String(r.username || "").toLowerCase().includes(term) ||
        String(r.full_name || "").toLowerCase().includes(term) ||
        String(r.phone || "").toLowerCase().includes(term) ||
        String(r.coupon_code || "").toLowerCase().includes(term)
    );
  }, [data.results, q]);

  return (
    <Box sx={{ maxWidth: 1240, mx: "auto", px: { xs: 1.5, sm: 2.5 }, py: 3 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 900, color: "#0F172A", letterSpacing: "-0.5px" }}>
              Activated Agent Training Vouchers
            </Typography>
            <Chip
              label="L7+ / 5 Directs"
              size="small"
              sx={{ bgcolor: "#DCFCE7", color: "#166534", fontWeight: 800, fontSize: "11px" }}
            />
          </Stack>
          <Typography variant="body2" sx={{ color: "#64748B", mt: 0.5 }}>
            Audit list of qualified agents whose first-purchase Agent Training Voucher (₹1,000) has been activated.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshRoundedIcon />}
          onClick={fetchData}
          disabled={loading}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            color: "#0F172A",
            borderColor: "#CBD5E1",
          }}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Metric Tiles */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 3,
            bgcolor: "#F8FAFC",
            border: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              bgcolor: "#DBEAFE",
              color: "#1E40AF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CardGiftcardRoundedIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
              Total Activated Vouchers
            </Typography>
            <Typography sx={{ fontSize: "22px", fontWeight: 900, color: "#0F172A" }}>
              {data.count || 0}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 3,
            bgcolor: "#F8FAFC",
            border: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              bgcolor: "#DCFCE7",
              color: "#166534",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MilitaryTechRoundedIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
              Total Face Value
            </Typography>
            <Typography sx={{ fontSize: "22px", fontWeight: 900, color: "#16A34A" }}>
              {fmtINR(data.total_value || (data.count || 0) * 1000)}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 3,
            bgcolor: "#F8FAFC",
            border: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              bgcolor: "#FEF3C7",
              color: "#B45309",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GroupRoundedIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
              Qualification Criterion
            </Typography>
            <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#0F172A", mt: 0.5 }}>
              Layer 7 Rank + 5 Directs
            </Typography>
          </Box>
        </Paper>
      </Stack>

      {/* Search & Filter */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: "1px solid #E2E8F0" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by user name, phone, user ID, or coupon code..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid #E2E8F0" }}>
        {loading ? (
          <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={36} />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#64748B", fontWeight: 600 }}>
              No activated agent training vouchers found.
            </Typography>
          </Box>
        ) : (
          <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ bgcolor: "#F8FAFC" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px" }}>AGENT / USER</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px" }}>PHONE</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px" }}>RANK LAYER</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px" }}>DIRECTS</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px" }}>COUPON CODE</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px" }}>FACE VALUE</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px" }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px" }}>ACTIVATED AT</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px" }} align="right">
                  TRAINING KIT
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={row.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 800, fontSize: "13px", color: "#0F172A" }}>
                      {row.full_name || row.username}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#64748B" }}>ID: {row.username}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: "12.5px", fontWeight: 600, color: "#334155" }}>
                    {row.phone || "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.current_rank || `L${row.rank_level}`}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        fontSize: "11px",
                        bgcolor: "#FAF5FF",
                        color: "#7E22CE",
                        border: "1px solid #E9D5FF",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${row.directs_count} Directs`}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        fontSize: "11px",
                        bgcolor: "#EFF6FF",
                        color: "#1D4ED8",
                        border: "1px solid #BFDBFE",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography sx={{ fontFamily: "monospace", fontSize: "12px", fontWeight: 700, color: "#0F172A" }}>
                        {row.coupon_code}
                      </Typography>
                      <Tooltip title={copiedId === row.id ? "Copied!" : "Copy Code"}>
                        <IconButton size="small" onClick={() => handleCopy(row.coupon_code, row.id)} sx={{ p: 0.3 }}>
                          {copiedId === row.id ? (
                            <CheckRoundedIcon sx={{ fontSize: 14, color: "#16A34A" }} />
                          ) : (
                            <ContentCopyRoundedIcon sx={{ fontSize: 14, color: "#64748B" }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: "13px", color: "#16A34A" }}>
                    ₹{Number(row.amount || 1000).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.status || "ACTIVATED"}
                      size="small"
                      sx={{
                        fontWeight: 900,
                        fontSize: "10.5px",
                        bgcolor: "#DCFCE7",
                        color: "#15803D",
                        border: "1px solid #BBF7D0",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: "12px", color: "#64748B" }}>{row.activated_at || "—"}</TableCell>
                  <TableCell align="right">
                    {row.pdf_url ? (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<PictureAsPdfRoundedIcon sx={{ fontSize: 15 }} />}
                        href={row.pdf_url}
                        target="_blank"
                        download
                        sx={{
                          textTransform: "none",
                          fontSize: "11.5px",
                          fontWeight: 700,
                          bgcolor: "#16A34A",
                          "&:hover": { bgcolor: "#15803D" },
                          borderRadius: 2,
                          py: 0.5,
                        }}
                      >
                        PDF Kit
                      </Button>
                    ) : (
                      <Typography sx={{ fontSize: "11px", color: "#94A3B8" }}>No PDF</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}
